/**
 * AdminCemeteryEditor — super-admin CRUD for the Cemeteries Directory.
 *
 * Backend: POST/PATCH/DELETE `/api/admin/cemeteries[/{slug}]`.
 * Every mutation is audit-logged. The list uses the public
 * `GET /api/cemeteries` so the same paging + filters as the public
 * directory work here too.
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Landmark, Plus, Trash2, Save, X } from "lucide-react";

const RELIGIONS = ["mixed", "christian", "muslim", "hindu", "sikh", "buddhist", "jain", "parsi", "jewish", "military", "municipal"];

const EMPTY = {
  name: "", aka: "", religion: "mixed",
  country: "India", state: "", city: "",
  latitude: "", longitude: "",
  established: "", burial_count: "", summary: "", featured: false,
};

function toPayload(f) {
  const out = { ...f };
  if (out.latitude === "") delete out.latitude; else out.latitude = Number(out.latitude);
  if (out.longitude === "") delete out.longitude; else out.longitude = Number(out.longitude);
  if (out.established === "") delete out.established; else out.established = Number(out.established);
  Object.keys(out).forEach((k) => { if (out[k] === "" || out[k] === null) delete out[k]; });
  return out;
}

export default function AdminCemeteryEditor() {
  const [items, setItems]   = useState([]);
  const [total, setTotal]   = useState(0);
  const [q, setQ]           = useState("");
  const [busy, setBusy]     = useState(false);
  const [editing, setEditing] = useState(null); // slug being edited or "new"
  const [form, setForm]     = useState(EMPTY);

  const load = () => {
    const params = new URLSearchParams();
    if (q.trim()) params.set("q", q.trim());
    params.set("limit", "100");
    http.get(`/cemeteries?${params}`)
      .then(({ data }) => { setItems(data.items || []); setTotal(data.total || 0); })
      .catch(() => setItems([]));
  };

  useEffect(load, [q]);

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (c) => {
    setEditing(c.slug);
    setForm({
      ...EMPTY,
      ...c,
      latitude: c.latitude ?? "",
      longitude: c.longitude ?? "",
      established: c.established ?? "",
    });
  };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Name is required");
    if (!form.country.trim()) return toast.error("Country is required");
    setBusy(true);
    try {
      if (editing === "new") {
        const { data } = await http.post("/admin/cemeteries", toPayload(form));
        toast.success(`Created "${data.name}"`);
      } else {
        const { data } = await http.patch(`/admin/cemeteries/${editing}`, toPayload(form));
        toast.success(`Updated "${data.name}"`);
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const del = async (c) => {
    if (!confirm(`Delete "${c.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await http.delete(`/admin/cemeteries/${c.slug}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-cemetery-editor">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Landmark className="w-4 h-4" style={{ color: "var(--wofy-primary)" }} />
          <h2 className="font-display text-2xl">Cemetery Editor <span className="text-sm opacity-60 font-normal">({total} total)</span></h2>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search cemeteries…"
            className="px-3 py-1.5 rounded-lg border text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="admin-cem-search"
          />
          <button type="button" onClick={startNew} className="wofy-btn-primary text-sm inline-flex items-center gap-1" data-testid="admin-cem-new">
            <Plus className="w-3.5 h-3.5" /> New
          </button>
        </div>
      </div>

      {editing && (
        <div className="wofy-card p-5 mb-6" style={{ borderColor: "var(--wofy-primary)" }} data-testid="admin-cem-form">
          <div className="overline mb-3" style={{ color: "var(--wofy-primary)" }}>{editing === "new" ? "ADD CEMETERY" : `EDIT · ${editing}`}</div>
          <div className="grid md:grid-cols-2 gap-3">
            {[
              ["name", "Name *", "text"],
              ["aka", "Also known as", "text"],
              ["country", "Country *", "text"],
              ["state", "State", "text"],
              ["city", "City", "text"],
              ["burial_count", "Burial count (label)", "text"],
              ["established", "Established (year)", "number"],
              ["latitude", "Latitude", "number"],
              ["longitude", "Longitude", "number"],
            ].map(([k, label, type]) => (
              <label key={k} className="text-xs block">
                <span className="opacity-70">{label}</span>
                <input
                  type={type}
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="mt-1 w-full px-3 py-2 rounded-lg border text-sm bg-transparent"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid={`admin-cem-field-${k}`}
                />
              </label>
            ))}
            <label className="text-xs block">
              <span className="opacity-70">Religion</span>
              <select
                value={form.religion}
                onChange={(e) => setForm({ ...form, religion: e.target.value })}
                className="mt-1 w-full px-3 py-2 rounded-lg border text-sm bg-transparent"
                style={{ borderColor: "var(--wofy-border)" }}
                data-testid="admin-cem-field-religion"
              >
                {RELIGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </label>
            <label className="text-xs block self-end">
              <input
                type="checkbox"
                checked={!!form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="mr-2 accent-current"
                data-testid="admin-cem-field-featured"
              />
              Featured (surfaces in directory hero)
            </label>
          </div>
          <label className="text-xs block mt-3">
            <span className="opacity-70">Summary</span>
            <textarea
              rows={3}
              value={form.summary}
              onChange={(e) => setForm({ ...form, summary: e.target.value })}
              className="mt-1 w-full px-3 py-2 rounded-lg border text-sm bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="admin-cem-field-summary"
            />
          </label>
          <div className="mt-4 flex items-center justify-end gap-2">
            <button type="button" onClick={cancel} className="wofy-btn-ghost text-sm inline-flex items-center gap-1"><X className="w-3.5 h-3.5" /> Cancel</button>
            <button type="button" onClick={save} disabled={busy} className="wofy-btn-primary text-sm inline-flex items-center gap-1" data-testid="admin-cem-save">
              <Save className="w-3.5 h-3.5" /> Save
            </button>
          </div>
        </div>
      )}

      <div className="wofy-card divide-y" style={{ borderColor: "var(--wofy-border)" }}>
        {items.map((c) => (
          <div key={c.slug} className="flex items-center justify-between gap-3 py-2.5 px-3 text-xs">
            <div className="min-w-0 flex-1">
              <div className="font-medium truncate">{c.name}</div>
              <div className="opacity-60 truncate">
                {c.religion} · {[c.city, c.state, c.country].filter(Boolean).join(" · ")}
                {c.established ? ` · est. ${c.established}` : ""}
              </div>
            </div>
            <button type="button" onClick={() => startEdit(c)} className="wofy-btn-ghost text-xs" data-testid={`admin-cem-edit-${c.slug}`}>Edit</button>
            <button type="button" onClick={() => del(c)} className="text-xs text-red-600 hover:underline inline-flex items-center gap-0.5" data-testid={`admin-cem-delete-${c.slug}`}>
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </div>
        ))}
        {items.length === 0 && <div className="p-4 text-xs opacity-60">No cemeteries match that search.</div>}
      </div>
    </div>
  );
}
